import * as React from "react";
import {
  OverlayToaster,
  Position,
  type OverlayToaster as OverlayToasterType,
} from "@blueprintjs/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiAdmin from "../../../api/admin";
import { FolderFormDialog } from "../../Admin/Organization/folders/FolderFormDialog";
import { DEACTIVATED_FOLDERS_QUERY_KEY } from "./folderAdminQueryKeys";

interface FolderAdminEditFolderDialogProps {
  folder: { id: number; label: string } | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export function FolderAdminEditFolderDialog(
  props: FolderAdminEditFolderDialogProps
) {
  const { folder, onClose, onUpdated } = props;
  const toaster = React.useRef<OverlayToasterType>(null);
  const queryClient = useQueryClient();

  const updateFolderMutation = useMutation({
    mutationFn: async ({ folderId, label }: { folderId: number; label: string }) => {
      return ApiAdmin.updateFolderForFolderAdmin(folderId, { label });
    },
    onError: (err: any) => {
      toaster.current?.show({
        message: err?.message || "Failed to update folder. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
    onSuccess: async () => {
      toaster.current?.show({
        message: "Folder updated successfully.",
        intent: "success",
        timeout: 3000,
      });
      await queryClient.invalidateQueries({
        queryKey: DEACTIVATED_FOLDERS_QUERY_KEY,
      });
    },
  });

  const handleSubmit = async (name: string) => {
    if (!folder) return;
    try {
      await updateFolderMutation.mutateAsync({
        folderId: folder.id,
        label: name,
      });
      onClose();
      onUpdated?.();
    } catch {
      // Error toast shown via onError; dialog stays open for retry
    }
  };

  return (
    <>
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <FolderFormDialog
        isOpen={folder !== null}
        onClose={onClose}
        title={folder ? `Edit Folder: ${folder.label}` : "Edit Folder"}
        initialValue={folder?.label ?? ""}
        isLoading={updateFolderMutation.isPending}
        loadingText="Saving..."
        submitText="Save"
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default FolderAdminEditFolderDialog;
